/* Formatting helpers. Public site uses Devanagari digits; the admin portal uses
 * Latin digits (easier to read phone numbers / IDs side by side). */

const DEVA = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];

export const toDeva = (v: string | number) => String(v).replace(/[0-9]/g, (d) => DEVA[+d]);

const IST = 'Asia/Kolkata';

/** "२६ सप्टेंबर २०२६" (public) — accepts YYYY-MM-DD or an ISO timestamp. */
export function formatDateMr(v: string | null | undefined, latinDigits = false): string {
  if (!v) return '—';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00+05:30`) : new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const s = new Intl.DateTimeFormat(latinDigits ? 'mr-IN-u-nu-latn' : 'mr-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: IST,
  }).format(d);
  return latinDigits ? s : toDeva(s);
}

/** "26 सप्टें. 2026, 4:05 PM" — admin tables. */
export function formatDateTimeAdmin(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return new Intl.DateTimeFormat('mr-IN-u-nu-latn', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: IST,
  }).format(d);
}

export const formatNumberAdmin = (n: number) => new Intl.NumberFormat('en-IN').format(n);
export const formatNumberMr = (n: number) => toDeva(new Intl.NumberFormat('en-IN').format(n));

/** Age in whole years on a given day (IST). */
export function ageFrom(dob: string | null | undefined, on = new Date()): number | null {
  if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) return null;
  const [y, m, d] = dob.split('-').map(Number);
  let age = on.getFullYear() - y;
  if (on.getMonth() + 1 < m || (on.getMonth() + 1 === m && on.getDate() < d)) age--;
  return age;
}

/** True only for a real calendar date YYYY-MM-DD (guards the backend's "infinity" quirk). */
export function isRealDate(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export const todayISO = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: IST }).format(new Date()); // YYYY-MM-DD

/** Candidate IDs are already short (e.g. PUN000001); this only trims legal-doc UUIDs for tight cells. */
export const shortId = (id: string) => id.slice(0, 8);
